import React, { useState, useRef, useEffect } from 'react';
import { useIdioma } from '../../i18n/idioma';
import { Send, X, Loader2, Bot, User, Plus, Maximize2, Lock, Globe, Mic } from 'lucide-react';
import LottiePlayer from '../ui/LottiePlayer';
import { IconoBotRaza } from '../ui/IconoBotRaza';
import { useDraggableFab } from '../../hooks/useDraggableFab';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useAIChat } from '../../contexts/AIChatContext';
import { useAIChatCRMContext } from '../../hooks/useAIChatCRMContext';
import { isVoiceEnabled } from '../../services/voiceAI';
import { usePendingChatBootstrap } from '../../hooks/usePendingChatBootstrap';
import { useChatAttachments } from '../../hooks/useChatAttachments';
import { renderMarkdown } from './renderMarkdown';
import AttachmentPickerButton from './AttachmentPickerButton';
import AttachmentThumb from './AttachmentThumb';
import RazaQuickPromptPicker from './RazaQuickPromptPicker';
import { RAZA_WELCOME_TITLE, RAZA_WELCOME_BODY, RAZA_INPUT_PLACEHOLDER } from './razaQuickPrompts';

/** Botón de ícono sobre la cabecera navy del panel. */
const headerIconBtn =
  'w-7 h-7 grid place-items-center rounded-lg text-acero-400 hover:text-brand-400 hover:bg-white/10 ' +
  'transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400';

const AIChatPanel: React.FC = () => {
    const { t } = useIdioma();
  const [input, setInput] = useState('');
  const [isDragHover, setIsDragHover] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { currentView } = useNavigation();
  const {
    messages,
    isLoading,
    activeConversation,
    isBubbleOpen: isOpen,
    setIsBubbleOpen: setIsOpen,
    sendMessage,
    createConversation,
    togglePrivacy,
    openFullView,
    openBubble,
    openContextualChat,
    openVoiceConversation,
    contextualLabel,
  } = useAIChat();

  const attachments = useChatAttachments(() => activeConversation?.id ?? null);
  const buildContext = useAIChatCRMContext();

  // Gated on the build-time voice kill switch as well as browser capability:
  // voice is OFF while the TTS server is down (see isVoiceEnabled).
  const isMicSupported = isVoiceEnabled()
    && typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

  // Text of the most recent assistant reply, for the screen-reader live region.
  const lastAssistantText = [...messages].reverse().find(m => m.role === 'assistant')?.text ?? '';

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  usePendingChatBootstrap({
    active: isOpen,
    buildContext,
    sendMessage,
    createConversation,
    setInput,
    focusInput: () => inputRef.current?.focus(),
    isLoading,
  });

  const handleSend = async (text?: string) => {
    const messageText = text || input.trim();
    if ((!messageText && !attachments.hasPending) || isLoading) return;
    setInput('');
    // Wait for uploads to settle (drops any that failed), then include in the message.
    const ready = attachments.hasPending ? await attachments.takeReadyAttachments() : undefined;
    await sendMessage(messageText, buildContext(), ready);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragHover(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) attachments.addFiles(files);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    createConversation();
    setInput('');
  };

  const handleOpenFullView = () => {
    setIsOpen(false);
    openFullView();
  };

  /* El boton se puede arrastrar y se queda donde lo dejen.
     Se llama ANTES del return temprano de abajo: los hooks no pueden ir
     detras de una salida condicional o React pierde el orden entre renders. */
  const fab = useDraggableFab({ size: 56, inset: 24 });

  if (currentView === 'AIAssistant') {
    return null;
  }

  return (
    <>
      {/* Floating Chat Button — primary entry; on mobile opens full chat page */}
      <button
        onClick={() => {
          const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
          if (isMobile) {
            openContextualChat({});
            return;
          }
          if (isOpen) setIsOpen(false);
          else openBubble();
        }}
        /* Cerrado va REDONDO, no cuadrado: el bot trae su propio disco amarillo,
           así que sobre un botón cuadrado del mismo amarillo se veían las
           esquinas y leía como dos formas superpuestas. Redondo, disco y botón
           se funden en una sola pieza. Abierto vuelve al cuadrado navy con la X. */
        className={`presionable fixed z-[241] w-14 h-14 shadow-s2 items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing
          transition-[background-color,border-radius] duration-150 flex focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 ${
          isOpen
            ? 'rounded-2xl bg-navy-850 text-white hover:bg-navy-700'
            : 'rounded-full bg-brand-400 text-navy-950 hover:bg-brand-300'
        }`}
        style={fab.style}
        {...fab.handlers}
        title={t('chat.boton.preguntar.title')}
        aria-label={isOpen ? t('chat.boton.cerrarburbuja.aria') : t('chat.boton.preguntar.aria')}
        aria-expanded={isOpen}
      >
        {/* Cerrado: el bot. Abierto: una X clara — el botón cambia de
            significado y la animación estorbaría.

            El archivo NO es un ciclo: medido fotograma a fotograma, el robot se
            mueve al entrar (f30–f65), se queda COMPLETAMENTE quieto 4 segundos
            (f65–f187) y luego se va. Repetirlo dejaba al robot entrando y
            saliendo cada rato, con el botón vacío en medio. Se toca la entrada
            una sola vez y se queda: `repetir={false}`.

            Del archivo se quitaron el globo de diálogo, el «HI!» y el disco
            amarillo. El disco lo pone el botón, y el globo a 56 px no se lee
            como globo — es una mancha oscura de cuatro píxeles asomando por el
            borde (de hecho pesaban 42 KB en PNG empotrado).

            El lienzo mide 2000 y el robot solo ocupa 973 de alto, centrado 125
            unidades por debajo del centro. De ahí salen los números: se dibuja
            a 70 px para que el robot mida 34 (el 61% del botón, la proporción
            de un icono en un FAB) y se sube 6 px, que es lo que el archivo lo
            tiene descentrado. Ambos medidos en pantalla, no calculados. */}
        {isOpen ? (
          <X size={24} aria-hidden="true" />
        ) : (
          /* El tramo 29–66 es el ENCENDIDO del robot, medido píxel a píxel:
             aparece en f29, las antenas se despliegan de −90° a 0° (f39–45) y
             la cara prende parpadeando (f48–51) hasta quedar al 100% en f65.
             De f65 a f187 el archivo se sostiene, y de f187 en adelante se
             apaga — ahí el botón quedaría vacío, por eso no se toca entero.

             `segmentoAlRepetir` = 45–66 porque en ese tramo lo ÚNICO que
             cambia es la opacidad de la cara: las antenas ya están desplegadas.
             O sea que repetir desde 45 es un parpadeo limpio, sin el salto que
             daría volver a 29 (antenas plegadas y cara apagada de golpe).

             Cada 9 s: lo justo para que se note que está vivo sin volverse un
             elemento que reclama atención. Entre parpadeos, `rp-bot-respira`
             (index.css) lo mantiene respirando. */
          <span className="rp-bot-respira block" style={{ marginTop: -6 }}>
            <LottiePlayer
              src="/assets/lottie/ai-chat-bot.json"
              size={70}
              segment={[29, 66]}
              segmentoAlRepetir={[45, 66]}
              repetir={false}
              repetirCada={3500}
              fallback={<IconoBotRaza size={24} className="text-navy-950" />}
            />
          </span>
        )}
      </button>

      {/* Chat Panel — desktop drawer only.
          z-240 puts the drawer above the record sheets (ClientDetail and
          EstimateDetail are z-220, their menus z-230) and below the true
          modals (ClientModal / PostSave at 250). Below 240 the "Ask Raza"
          card inside a client sheet opened the drawer BEHIND the sheet and
          its scrim, so the button looked broken. HelpVideoBox moved to 245
          in the same change — it is fixed at the identical bottom-right
          corner and would otherwise be covered.

          `inert` when closed: the drawer is only hidden with opacity and
          pointer-events, which hides it from the mouse but not from the tab
          order or a screen reader — every control and the whole welcome
          message stayed reachable and were read out on a page that appeared
          to have no chat open. */}
      <div
        role="dialog"
        aria-label="Raza AI"
        aria-hidden={!isOpen}
        inert={!isOpen}
        className={`hidden md:flex fixed z-[240] w-[400px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-s3 border border-acero-200 flex-col overflow-hidden transition-[opacity,transform] duration-200 ease-out ${
          isOpen
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-4 pointer-events-none'
        }`}
        /* Hangs off the button, centred on it.
           Pinned to a screen corner instead, a button dragged to the top-middle
           opened its panel over on the right with nothing joining the two — it
           read as some unrelated thing appearing. Clamped so a button near an
           edge still gets a panel fully on screen, and it opens away from
           whichever half of the screen the button is in. */
        style={{
          height: 'min(600px, calc(100vh - 8rem))',
          ...(fab.anchorPanel(400) ?? { right: 24, bottom: 96 }),
        }}
      >
        {/* Header */}
        <div className="bg-navy-850 px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {/* El mismo robot del botón flotante, no una chispa aparte.
                Números heredados de allí: el lienzo mide 2000 y el robot ocupa
                973, o sea que se dibuja al doble del alto que quieres — 33 para
                que mida 16, la mitad de la teja. El archivo lo tiene 125
                unidades por debajo del centro, que a esta escala son 3 px.
                Aquí NO repite: es una marca de identidad de la cabecera, y con
                la bienvenida parpadeando justo debajo, dos robots fuera de
                fase serían ruido. Toca su entrada una vez y se queda. */}
            <div className="w-8 h-8 rounded-lg bg-brand-400 grid place-items-center shrink-0 overflow-hidden">
              <span className="block" style={{ marginTop: -3 }}>
                <LottiePlayer
                  src="/assets/lottie/ai-chat-bot.json"
                  size={33}
                  segment={[29, 66]}
                  repetir={false}
                  fallback={<IconoBotRaza size={16} className="text-navy-950" />}
                />
              </span>
            </div>
            <div className="min-w-0">
              <h3 className="text-white font-semibold tracking-tight text-sm">Raza AI</h3>
              <p className="text-acero-400 text-[11px] truncate max-w-[180px]">
                {contextualLabel || t('chat.cabecera.subtitulo')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {/* Privacy toggle */}
            {activeConversation && (
              <button
                onClick={togglePrivacy}
                className={headerIconBtn}
                title={activeConversation.is_private ? t('chat.privacidad.privada.title') : t('chat.privacidad.compartida.title')}
                aria-label={activeConversation.is_private ? t('chat.privacidad.privada.aria') : t('chat.privacidad.compartida.aria')}
              >
                {activeConversation.is_private ? <Lock size={13} aria-hidden="true" /> : <Globe size={13} aria-hidden="true" />}
              </button>
            )}
            {/* New Chat */}
            <button
              onClick={handleNewChat}
              className={headerIconBtn}
              title={t('chat.boton.nueva.title')}
              aria-label={t('chat.boton.nueva.aria')}
            >
              <Plus size={14} aria-hidden="true" />
            </button>
            {/* Open in full view */}
            <button
              onClick={handleOpenFullView}
              className={headerIconBtn}
              title={t('chat.boton.pantallacompleta.title')}
              aria-label={t('chat.boton.pantallacompleta.aria')}
            >
              <Maximize2 size={13} aria-hidden="true" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className={headerIconBtn}
              title={t('chat.boton.cerrar.title')}
              aria-label={t('chat.boton.cerrar.aria')}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div
          className={`relative flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3 custom-scrollbar bg-acero-50 ${
            isDragHover ? 'ring-2 ring-brand-400 ring-inset' : ''
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragHover(true); }}
          onDragLeave={() => setIsDragHover(false)}
          onDrop={handleDrop}
        >
          {messages.length === 0 ? (
            // Welcome State
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              {/* El foco de la pantalla vacía: aquí SÍ parpadea. Mismo cálculo
                  que la cabecera — 48 para que el robot mida 23 (la mitad de la
                  teja) y −4 px de descentrado del archivo. `repetirCada` y
                  `segmentoAlRepetir` copiados del botón flotante: el tramo
                  45–66 es solo el parpadeo de la cara, con las antenas ya
                  desplegadas, así que repetirlo no da el salto de volver a
                  plegarlas. Cada 9 s: se nota que está vivo sin pedir atención. */}
              <div className="w-12 h-12 rounded-2xl bg-brand-400 grid place-items-center mb-4 overflow-hidden">
                <span className="rp-bot-respira block" style={{ marginTop: -4 }}>
                  <LottiePlayer
                    src="/assets/lottie/ai-chat-bot.json"
                    size={48}
                    segment={[29, 66]}
                    segmentoAlRepetir={[45, 66]}
                    repetir={false}
                    repetirCada={3500}
                    fallback={<IconoBotRaza size={22} className="text-navy-950" />}
                  />
                </span>
              </div>
              <h4 className="text-navy-950 font-semibold tracking-tight text-lg mb-1">{t(RAZA_WELCOME_TITLE)}</h4>
              <p className="text-acero-500 text-sm mb-5 leading-relaxed">
                {t(RAZA_WELCOME_BODY)}
              </p>
              <RazaQuickPromptPicker compact onSelect={(p) => handleSend(p)} />
            </div>
          ) : (
            // Message Bubbles
            <>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  {/* Avatar */}
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                    msg.role === 'user'
                      ? 'bg-navy-850 text-white'
                      : 'bg-brand-400 text-navy-950'
                  }`}>
                    {msg.role === 'user' ? <User size={14} aria-hidden="true" /> : <Bot size={14} aria-hidden="true" />}
                  </div>

                  {/* Bubble */}
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-navy-850 text-white rounded-tr-md'
                        : 'bg-white border border-acero-200 text-navy-950 rounded-tl-md'
                    }`}
                  >
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {msg.attachments.map((att, i) => (
                          <a key={`${msg.id}-att-${i}`} href={att.url} target="_blank" rel="noopener noreferrer">
                            <AttachmentThumb
                              attachment={{ kind: 'committed', url: att.url, mimeType: att.mimeType, name: att.name }}
                              size="sm"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                    {msg.role === 'assistant' ? (
                      <div className="prose-sm">{renderMarkdown(msg.text)}</div>
                    ) : msg.text ? (
                      <p>{msg.text}</p>
                    ) : null}
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div className="flex gap-2">
                  <div className="w-7 h-7 rounded-full bg-brand-400 flex items-center justify-center shrink-0">
                    <Bot size={14} className="text-navy-950" aria-hidden="true" />
                  </div>
                  <div className="bg-white border border-acero-200 rounded-2xl rounded-tl-md px-4 py-3">
                    <div className="flex items-center gap-1.5" role="status">
                      {/* A live region announces its TEXT when that text
                          changes. This one held three empty dots and an
                          aria-label, so a screen-reader user got no signal at
                          all that Raza was working. */}
                      <span className="sr-only">{t('chat.estado.escribiendo')}</span>
                      <div className="w-2 h-2 bg-brand-400 rounded-full animate-bounce" aria-hidden="true" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-brand-400 rounded-full animate-bounce" aria-hidden="true" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-brand-400 rounded-full animate-bounce" aria-hidden="true" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Announces the finished reply. The transcript itself is not a
                  live region — marking it one would re-read the whole
                  conversation on every append. */}
              <p className="sr-only" role="status" aria-live="polite">
                {!isLoading && lastAssistantText ? lastAssistantText : ''}
              </p>

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area */}
        <div className="shrink-0 border-t border-acero-200 bg-white px-3 py-3">
          {/* Pending attachment thumbnails */}
          {attachments.hasPending && (
            <div className="flex flex-wrap gap-2 mb-2 px-1">
              {attachments.pending.map(p => (
                <AttachmentThumb
                  key={p.localId}
                  attachment={{ kind: 'pending', data: p }}
                  onRemove={() => attachments.removeOne(p.localId)}
                  size="sm"
                />
              ))}
            </div>
          )}

          {/* Normal input */}
          <div
            id="ai-chat-input-anchor"
            className="flex items-center gap-1.5 bg-white border border-acero-300 rounded-xl px-2 py-1.5
              transition-[border-color,box-shadow] duration-150
              focus-within:border-navy-600 focus-within:ring-2 focus-within:ring-brand-400/55"
          >
            <AttachmentPickerButton
              currentCount={attachments.pending.length}
              onFilesSelected={attachments.addFiles}
              disabled={isLoading}
              size="sm"
            />
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t(RAZA_INPUT_PLACEHOLDER)}
              aria-label={t('chat.input.aria')}
              className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-navy-950 placeholder:text-acero-400 font-medium py-1.5"
              disabled={isLoading}
            />
            {/* Mic button — opens the plasma orb voice loop */}
            {isMicSupported && (
              <button
                onClick={openVoiceConversation}
                disabled={isLoading}
                className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-150
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
                  isLoading
                    ? 'text-acero-300 cursor-not-allowed'
                    : 'text-acero-500 hover:bg-acero-100 hover:text-navy-950 active:scale-[0.98]'
                }`}
                title={t('chat.boton.voz.title')}
                aria-label={t('chat.boton.voz.aria')}
              >
                <Mic size={14} aria-hidden="true" />
              </button>
            )}
            {/* Send button */}
            <button
              onClick={() => handleSend()}
              disabled={(!input.trim() && !attachments.hasPending) || isLoading}
              aria-label={t('chat.boton.enviar.aria')}
              className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-150
                focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
                (input.trim() || attachments.hasPending) && !isLoading
                  ? 'bg-brand-400 text-navy-950 hover:bg-brand-300 active:scale-[0.98]'
                  : 'bg-acero-100 text-acero-400 cursor-not-allowed'
              }`}
            >
              {isLoading ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
            </button>
          </div>


          <p className="text-center text-[11px] text-acero-400 mt-2">
            {t('chat.pie.tecnologia')}
          </p>
        </div>
      </div>
    </>
  );
};

export default AIChatPanel;
